/**
 * INPC y actualización de pérdidas fiscales (Art. 57 LISR) — espejo del backend.
 * Primera: monto × (INPC dic / INPC jul) del año de origen.
 * Subsecuente: monto_1 × (INPC jun aplicación / INPC última actualización; default dic origen).
 */
import { TAX_SUMMARY_MONTH_KEYS } from "@/lib/taxSummaryWorkbookConstants";

export const INPC_MONTH_KEYS = TAX_SUMMARY_MONTH_KEYS.filter((k) => k !== "acumulado");

/** Semilla base 2ª Q Jul 2018 (misma que backend). El usuario puede editar/completar meses. */
export const SEED_INPC_BY_YEAR = {
  2017: {
    ene: "93.604", feb: "94.145", mar: "94.722", abr: "94.839",
    may: "94.725", jun: "94.964", jul: "95.323", ago: "95.794",
    sep: "96.094", oct: "96.698", nov: "97.695", dic: "98.273",
  },
  2018: {
    ene: "98.795", feb: "99.171", mar: "99.492", abr: "99.155",
    may: "98.994", jun: "99.376", jul: "99.909", ago: "100.492",
    sep: "100.917", oct: "101.440", nov: "102.303", dic: "103.020",
  },
  2019: {
    ene: "103.108", feb: "103.079", mar: "103.476", abr: "103.531",
    may: "103.233", jun: "103.299", jul: "103.687", ago: "103.670",
    sep: "103.942", oct: "104.503", nov: "105.346", dic: "105.934",
  },
  2020: {
    ene: "106.447", feb: "106.889", mar: "106.838", abr: "105.755",
    may: "106.162", jun: "106.743", jul: "107.444", ago: "107.867",
    sep: "108.114", oct: "108.774", nov: "108.856", dic: "109.271",
  },
  2021: {
    ene: "110.210", feb: "110.907", mar: "111.824", abr: "112.190",
    may: "112.419", jun: "113.018", jul: "113.682", ago: "113.899",
    sep: "114.601", oct: "115.561", nov: "116.884", dic: "117.308",
  },
  2022: {
    ene: "118.002", feb: "118.981", mar: "120.159", abr: "120.809",
    may: "121.022", jun: "122.044", jul: "122.948", ago: "123.803",
    sep: "124.571", oct: "125.276", nov: "125.997", dic: "126.478",
  },
  2023: {
    ene: "127.336", feb: "128.046", mar: "128.389", abr: "128.363",
    may: "128.084", jun: "128.214", jul: "128.832", ago: "129.545",
    sep: "130.120", oct: "130.609", nov: "131.445", dic: "132.373",
  },
  2024: {
    ene: "133.555", feb: "133.681", mar: "134.065", abr: "134.336",
    may: "134.087", jun: "134.594", jul: "136.003", ago: "136.013",
    sep: "136.080", oct: "136.828", nov: "137.424", dic: "137.949",
  },
  2025: {
    ene: "138.343", feb: "138.726", mar: "139.161", abr: "139.620",
    may: "140.012", jun: "140.405", jul: "140.780", ago: "140.867",
    sep: "141.197", oct: "141.708", nov: "142.645", dic: "143.042",
  },
  2026: {
    ene: "143.588", feb: "144.307", mar: "145.544", abr: "145.831",
    may: "145.527", jun: "", jul: "", ago: "",
    sep: "", oct: "", nov: "", dic: "",
  },
};

function emptyYearMonths() {
  return Object.fromEntries(INPC_MONTH_KEYS.map((mk) => [mk, ""]));
}

export function seedIndicesInpc(throughYear, yearsBack = 10) {
  const end = Number(throughYear) || Math.max(...Object.keys(SEED_INPC_BY_YEAR).map(Number));
  const start = end - Math.max(1, yearsBack) + 1;
  const out = {};
  for (let year = start; year <= end; year += 1) {
    const seeded = SEED_INPC_BY_YEAR[year] || {};
    const row = emptyYearMonths();
    INPC_MONTH_KEYS.forEach((mk) => {
      row[mk] = String(seeded[mk] || "").trim();
    });
    out[String(year)] = row;
  }
  return out;
}

export function ensureIndicesInpc(raw, workbookYear) {
  const base = seedIndicesInpc(workbookYear);
  if (!raw || typeof raw !== "object") return base;
  Object.entries(raw).forEach(([yearKey, months]) => {
    const year = Number(yearKey);
    if (!Number.isFinite(year) || !months || typeof months !== "object") return;
    const row = base[String(year)] || emptyYearMonths();
    INPC_MONTH_KEYS.forEach((mk) => {
      const text = String(months[mk] ?? "").trim();
      if (text) row[mk] = text;
    });
    base[String(year)] = row;
  });
  return Object.fromEntries(
    Object.keys(base)
      .sort((a, b) => Number(a) - Number(b))
      .map((k) => [k, base[k]]),
  );
}

function coerceFloat(value) {
  const n = Number(String(value ?? "").replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : 0;
}

function formatFactor(n) {
  if (!Number.isFinite(n)) return "";
  return roundFactor(n).toFixed(4);
}

function formatAmount(n) {
  if (!Number.isFinite(n)) return "";
  const rounded = Math.round(n * 100) / 100;
  return String(rounded);
}

function getInpc(indices, year, monthKey) {
  const row = indices?.[String(year)];
  if (!row) return null;
  const n = coerceFloat(row[monthKey]);
  return n > 0 ? n : null;
}

function roundFactor(n) {
  return Math.round(n * 10000) / 10000;
}

export function calcularActualizacionPerdida({
  montoInicial,
  anioOrigen,
  anioAplicacion,
  indices,
  ultimaActAnio,
  ultimaActMes,
} = {}) {
  const initial = coerceFloat(montoInicial);
  const origin = Number(anioOrigen);
  const applyYear = Number(anioAplicacion);
  const empty = {
    factorPrimera: "",
    montoPrimera: "",
    factorSubsecuente: "",
    montoActualizado: "",
    missing: [],
  };
  if (initial <= 0 || !Number.isFinite(origin)) return empty;

  const missing = [];
  const inpcJul = getInpc(indices, origin, "jul");
  const inpcDic = getInpc(indices, origin, "dic");
  if (inpcJul == null) missing.push(`${origin}-jul`);
  if (inpcDic == null) missing.push(`${origin}-dic`);
  if (inpcJul == null || inpcDic == null) return { ...empty, missing };

  const f1 = roundFactor(inpcDic / inpcJul);
  const montoPrimera = initial * f1;
  const result = {
    factorPrimera: formatFactor(f1),
    montoPrimera: formatAmount(montoPrimera),
    factorSubsecuente: "",
    montoActualizado: formatAmount(montoPrimera),
    missing,
  };

  if (!Number.isFinite(applyYear) || applyYear <= origin) return result;

  let lastYear = Number(ultimaActAnio);
  if (!Number.isFinite(lastYear)) lastYear = origin;
  let lastMonth = String(ultimaActMes || "dic").trim().toLowerCase();
  if (!INPC_MONTH_KEYS.includes(lastMonth)) lastMonth = "dic";

  const inpcJun = getInpc(indices, applyYear, "jun");
  const inpcLast = getInpc(indices, lastYear, lastMonth);
  if (inpcJun == null) missing.push(`${applyYear}-jun`);
  if (inpcLast == null) missing.push(`${lastYear}-${lastMonth}`);
  result.missing = missing;
  if (inpcJun == null || inpcLast == null) return result;

  const f2 = roundFactor(inpcJun / inpcLast);
  result.factorSubsecuente = formatFactor(f2);
  result.montoActualizado = formatAmount(montoPrimera * f2);
  return result;
}

export function sumAplicado(entry) {
  const aplicado = entry?.aplicado || {};
  return INPC_MONTH_KEYS.reduce((acc, mk) => acc + coerceFloat(aplicado[mk]), 0);
}

export function saldoPendienteDesdeActualizado(entry, montoActualizado) {
  const actualizado = coerceFloat(montoActualizado);
  if (actualizado <= 0) return "";
  return formatAmount(Math.max(0, actualizado - sumAplicado(entry)));
}
